import './styles.scss';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useState } from 'react';

import { AnimatedTransition } from '../../../../../../components/animated-transition';
import { BackgroundMessage } from '../../../../../../components/background-message';
import { PORTFOLIO_MESSAGES } from '../../../../../../constants/reports/reports-messages';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../../providers/portfolio-management';
import { TransactionsFiltersContext } from '../../../../../../providers/transactions/transactions-filters-provider';
import { TransactionsContext } from '../../../../../../providers/transactions/transactions-provider';
import { IAccountOption } from '../../../../../../providers/venue-provider';
import { CheckLastUpdate } from '../../../../components/check-last-update';

interface IProps {
  data: any[];
  header: any[];
  lastUpdateTransactions: string;
  hasOnFilterData: boolean;
  accountOption: IAccountOption;
}

const TransactionTable = (props: IProps) => {
  const { data, header, lastUpdateTransactions, hasOnFilterData, accountOption } = props;
  const [expandableRows, setExpandableRows] = useState<any[]>([]);
  const { getData, isLoadingTransactions } = useContext(TransactionsContext);
  const {
    filters: { errorTransactions },
  } = useContext(TransactionsFiltersContext);
  const { selectedDataDisplayOption, aggregatedData } = useContext(PortfolioManagementContext);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const handleExpandable = (id: string) => {
    if (!expandableRows.includes(id)) {
      setExpandableRows((prev) => [...prev, id]);
    } else {
      setExpandableRows((prev) => prev.filter((item) => item !== id));
    }
  };

  const renderDataLeft = (block: any[]) => {
    const renderChildrenTableLeft = (arr: any[], level: number) => {
      return arr.map((item, index) => {
        if (!!item.children && item.children.length) {
          return renderNodeItem(item, level, `${item.id}_${index}`);
        } else {
          const hasCashflow = item['cashflow'] !== undefined;
          const hasRpl = hasCashflow && !!item['session_rpl']?.length;
          const hasUpl = hasCashflow && !!item['session_upl']?.length;

          const classNameSize = hasRpl && hasUpl ? 'size2' : hasRpl || hasUpl ? 'size1' : '';

          return (
            <tr className={`level-${level}`} key={`${item.id}_${index}`}>
              <td style={{ width: '1000px' }} className={item.id === 'spot' ? 'spot' : ''}>
                <div
                  className={`table-item ${classNameSize}`}
                  style={{
                    paddingLeft: `${16 * level}px`,
                    width: '100%',
                  }}
                >
                  <span className="label first-label">{item.label}</span>
                  <span className="label">{/* {item.position} */}</span>
                </div>
              </td>
            </tr>
          );
        }
      });
    };

    const renderNodeItem = (item: any, level: number, key: string) => {
      const childrenLength = item.children ? item.children.length : 0;
      return (
        <tr style={{ width: '100%' }} className={`level-${level}`} key={key}>
          <td style={{ paddingBottom: 0, width: '10000px' }}>
            <div className="cell-node">
              <div
                onClick={() => handleExpandable(item.id)}
                className="node-button"
                style={{
                  paddingLeft: `${16 * level}px`,
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`icon ${expandableRows.includes(item.id) && 'rotate'}`}
                />
                <span className="label first-label">
                  {item.label}
                  {childrenLength > 0 && ` (${childrenLength})`}
                </span>
                <span className="label">{item.position}</span>
              </div>
              <div className={`items level-${level}`}>
                {expandableRows.includes(item.id) &&
                  item.children &&
                  renderChildrenTableLeft(item.children, level + 1)}
              </div>
            </div>
          </td>
        </tr>
      );
    };

    return renderChildrenTableLeft(block, 0);
  };

  const renderDataContent = (block: any[]) => {
    const items: any[] = [];

    const dataLoop = (item: any, parent: string[], level: number) => {
      items.push({
        id: item.id,
        label: item.label,
        parent,
        values: item.values,
        level: level,
        hasChildren: !!item.children && item.children.length,
      });

      if (!!item.children && item.children.length) {
        item.children.forEach((child: any) => {
          dataLoop(child, [...parent, item.id], level + 1);
        });
      }
    };

    block.forEach((item) => {
      dataLoop(item, [], 0);
    });

    return items.map((item, index) => {
      let render = true;

      item.parent.forEach((parent: any) => {
        if (!expandableRows.includes(parent)) {
          render = false;
        }
      });

      if (!render) return null;

      return (
        <tr className={`level-${item.level}`} key={`${item.id}__${index}`}>
          {item.values.map((val: any, i: number) => {
            if (item.level === 0 || item.level === 1) {
              if (i === 0) {
                return <td key={`${Math.random()}_${i}`}></td>;
              }
            }
            if (item.hasChildren && (i === 7 || i === 6)) {
              return <td key={`${Math.random()}_${i}`}></td>;
            }

            return <td key={`${val}_${i}`}>{val}</td>;
          })}
        </tr>
      );
    });
  };

  useEffect(() => {
    if (!data || data.length === 0 || data[0]?.length === 0) {
      setExpandableRows([]);
      return;
    }

    const firstGroup = data[0];
    if (firstGroup[0]?.children && firstGroup[0]?.children.length > 0) {
      const firstItem = firstGroup[0]?.children[0];
      setExpandableRows([firstGroup[0].id, firstItem.id]);
    } else {
      setExpandableRows([]);
    }
  }, [data]);

  const callAggregatedData = () => {
    const AggregatedAccountsFilteredByExchangeDeribit = aggregatedData.accounts.filter(
      (account) => account.exchange === 'deribit',
    );

    if (AggregatedAccountsFilteredByExchangeDeribit.length > 0) {
      getData(AggregatedAccountsFilteredByExchangeDeribit, aggregatedData.accounts);
    }
  };

  const consoleError = console.error;

  console.error = (err: any) => {
    if (err && (typeof err !== 'string' || !err.includes('cannot appear as a child'))) {
      consoleError(err);
    }
  };

  return (
    <div className="transaction-table-container">
      {!isLoadingTransactions && !errorTransactions && data && (
        <>
          <CheckLastUpdate lastUpdate={lastUpdateTransactions} />
          <div className="transaction-table">
            <AnimatedTransition className="left-table">
              <table>
                <thead>
                  <tr>
                    <th key="instrument">Instrument</th>
                  </tr>
                </thead>

                {data?.map((block, index) => {
                  return <tbody key={`left_block_${index}`}>{renderDataLeft(block)}</tbody>;
                })}
              </table>
            </AnimatedTransition>

            <AnimatedTransition className="container-content-table">
              <table className="content-table">
                <thead>
                  <tr>
                    {header.map((column) => (
                      <th key={column} style={{ width: '88px' }}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                {data?.map((block, index) => {
                  return <tbody key={`content_block_${index}`}>{renderDataContent(block)}</tbody>;
                })}
              </table>
            </AnimatedTransition>
          </div>
        </>
      )}

      {isLoadingTransactions && <BackgroundMessage isLoading />}

      {!isLoadingTransactions && errorTransactions && (
        <BackgroundMessage
          message={PORTFOLIO_MESSAGES.ERROR_REQUEST_TRANSACTIONS}
          type="error"
          button={{
            title: PORTFOLIO_MESSAGES.ERROR_REQUEST_TRANSACTIONS.button,
            onClick: () =>
              isDataDisplayPerAccount ? getData(accountOption) : callAggregatedData(),
          }}
        />
      )}

      {!isLoadingTransactions && !errorTransactions && !data && (
        <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_DATA_FOUND_TRANSACTIONS} top="0" />
      )}
      {!isLoadingTransactions && !errorTransactions && !hasOnFilterData && (
        <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_DATA_FOUND_FILTER_TRANSACTIONS} top="0" />
      )}
    </div>
  );
};

export default TransactionTable;
